import { Loader as GoogleMapsLoader } from '@googlemaps/js-api-loader';
import type { ICoords, ILocationEntity, LocationSettingsType } from '../types/location.types';
import { locationsRepository, geocoder, awaitingLocations, awaitingUserLocation } from '../components/bootstrap/location/bootstrap';
import { sortByDistance } from '../components/bootstrap/location/methods';
import { listen } from '@city-mattress/js-utils';
import { EventBus } from '@city-mattress/js-utils/EventBus';
import type { IEventBus } from '@city-mattress/js-utils/EventBus';

const bus: IEventBus = new EventBus();

const parser = new DOMParser();

const SECTION_CONFIG = window['CM_SECTION_CONFIG']['STORES_LIST_HEADER'];

const mapID = SECTION_CONFIG.mapId;

//event name used by the lookup form
const LOOKUP_EVENT_NAME = SECTION_CONFIG.lookupEventName || 'CMGEO_GOOGLE_MAPS_LOCATION';

//Custom marker SVG
const pinHTML = document.getElementById(`${mapID}Pin`).innerHTML;

const infoWindowHTML = document.getElementById(`${mapID}Info`).innerHTML;
const infoWindowDomElement = parser.parseFromString(infoWindowHTML, 'text/html');

/**
 * Hydrates the info window from the marker with location data
 * @param location
 */
const hydrateInfoWindow = function (location: ILocationEntity) {
	infoWindowDomElement.getElementById('MapInfoName').innerHTML = location.name;
	infoWindowDomElement.getElementById('MapInfoAddress').innerHTML =
		`${location.address.line1}<br>${location.address.city}, ${location.address.state} ${location.address.zip}`;
	infoWindowDomElement.getElementById('MapInfoDirections').setAttribute('href', location.googleMapsLink);	
	infoWindowDomElement.getElementById('MapInfoDetails').setAttribute('href', location.url);	


	return infoWindowDomElement.documentElement.innerHTML;
};

//google maps loader
const settings: LocationSettingsType = window['CM_LOCATIONS_SETTINGS'];
const mapLoaderOptions = {
	apiKey: settings.google_api_key,
	version: 'weekly'
};
const mapsLoader: GoogleMapsLoader = new GoogleMapsLoader(mapLoaderOptions);

/**
 * Waits for our map to build
 */
const awaitingMap = new Promise((resolve) => {
	const mapFactory = async function () {
		const mapsLibrary = await mapsLoader.importLibrary('maps');
		const { Map, InfoWindow } = mapsLibrary;
		const { AdvancedMarkerElement } = await mapsLoader.importLibrary('marker');

		return { Map, InfoWindow, AdvancedMarkerElement };
	};

	geocoder.getLocation().then(async (coords) => {
		const { Map, AdvancedMarkerElement, InfoWindow } = await mapFactory();
		const map = new Map(document.getElementById(mapID), {
			center: { lat: coords.latitude, lng: coords.longitude },
			zoom: 11,
			mapId: 'fae3634d96247524'
		});
		resolve([map, AdvancedMarkerElement, InfoWindow]);
	});
});

document.addEventListener('alpine:init', () => {
	const Alpine = window['Alpine'];

	Alpine.data('mapStoresList', () => ({
		list: [],
		me: <ICoords>null,
		loading: true,
		selectedIndex: null,
		showAllStores: false,
		select(index) {
			this.selectedIndex = index;
		},
		init() {


			bus.subscribe('MarkerStoreSelected', function(location: ILocationEntity) {
				document.getElementById(`LocationListItem${location.storisId}`).click();
			});

			/**
			 * Boots the map and the list and makes sure all is loaded to allow other components to function
			 */
			const awaitingAll = Promise.all([awaitingLocations, awaitingUserLocation, awaitingMap]).then((values) => {
				const locations = values[0];
				const userLocation = values[1];

				this.list = locations.sort(sortByDistance);
				this.me = userLocation;

				const map = values[2][0];
				const AdvancedMarkerElement = values[2][1];
				const InfoWindow = values[2][2];

				const infoWindow = new InfoWindow({
					ariaLabel: null,
					content: ''
				});

				locations.forEach((location) => {
					const marker = new AdvancedMarkerElement({
						position: { lat: location.latitude, lng: location.longitude },
						map,
						title: location.name,
						gmpClickable: true
					});

					marker.addEventListener('gmp-click', (e) => {
						const { target } = e;
						infoWindow.close();
						infoWindow.setContent(hydrateInfoWindow(location));
						infoWindow.open(marker.map, marker);
						bus.publish('MarkerStoreSelected', location);
					});
				});

				return values;
			});

			awaitingAll.then((values) => {
				console.log('RESOLVED', values);
				this.loading = false;
			});

			//Listen to a change in the map location to recenter
			listen(LOOKUP_EVENT_NAME, (event) => {
				this.loading = true;

				awaitingAll.then((values) => {
					const map = values[2][0];
					const { zipcode, latitude, longitude } = <ICoords>event.detail;

					locationsRepository.get().then((locations: ILocationEntity[]) => {
						locations.forEach((location: ILocationEntity) => {
							location.assertDistance(event.detail);
							console.log(location);
						});

						this.list = [...locations];
						this.list.sort(sortByDistance);

						this.me = event.detail;

						map.setCenter({ lat: latitude, lng: longitude });

						setTimeout(() => {
							this.loading = false;
						}, 500);
					});
				});
			});
		}
	}));
});
